<template>
	<div class="entry-table">
		<div class="flex-table" role="table-head">
			<div
				:class="parent && parent.parentPoId ? 'flex-table-row' : 'flex-table-row parent-item'"
				role="row-group"
			>
				<div class="flex-table-cell text-center" role="column-header">&nbsp;</div>
				<div class="flex-table-cell" role="column-header">STYLE CODE</div>
				<div class="flex-table-cell" role="column-header">DESCRIPTION</div>
				<div class="flex-table-cell text-center" role="column-header">SIZE</div>
				<div class="flex-table-cell text-center" role="column-header">ORDERED</div>
				<div class="flex-table-cell text-center" role="column-header">
					{{ dropShip ? "" : "RECEIVED PRIOR" }}
				</div>
				<div class="flex-table-cell text-center" role="column-header">
					{{ dropShip ? "" : "QTY TO RECEIVE" }}
				</div>
			</div>
		</div>
		<div class="flex-table" role="table-body">
			<div
				v-for="(data, i) in localTableData"
				:key="i"
				:class="parent && parent.parentPoId ? 'flex-table-row' : 'flex-table-row parent-item'"
				role="row-group"
			>
				<div class="d-flex flex-table-cell justify-center" role="cell">
					<v-tooltip left v-if="dropShip">
						<template v-slot:activator="{ on, attrs }">
							<v-icon color="primary" dark v-bind="attrs" v-on="on"> mdi-parachute </v-icon>
						</template>
						<span>Dropship</span>
					</v-tooltip>
					<v-checkbox
						v-else
						v-model="data.selected"
						class="form-checkbox-center"
						color="accent"
						hide-details="auto"
						:disabled="!canReceive"
					>
					</v-checkbox>
				</div>
				<div class="d-flex flex-table-cell" role="cell">
					<v-icon v-if="parent && parent.parentPoId" class="mr-4"
						>mdi-subdirectory-arrow-right</v-icon
					>
					<SystemCategoryIcon :system-category-id="data.systemCategoryId" />
					{{ data.itemOrdered.styleCode }}
				</div>
				<div class="d-flex flex-table-cell" role="cell">
					{{ data.itemOrdered.description }}
				</div>
				<div class="flex-table-cell text-center" role="cell">
					<div class="size-quantity-wrapper">
						<div
							v-for="(sizeItem, idx) in combinedSizes(data)"
							:key="idx"
							class="size-quantity-row"
						>
							{{ sizeItem.size }}<span v-if="sizeItem.length">/{{ sizeItem.length }}</span>
						</div>
					</div>
				</div>
				<div class="flex-table-cell text-center" role="cell">
					<div class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0" class="size-quantity-row">
							{{ data.quantity }}
						</div>
						<div v-else>
							<div
								v-for="(sizeItem, idx) in combinedSizes(data)"
								:key="idx"
								class="size-quantity-row"
							>
								{{ sizeItem.quantity }}
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div v-if="!dropShip" class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0" class="size-quantity-row">
							<v-text-field
								class="number-input"
								outlined
								dense
								hide-details="auto"
								v-model.number="data.quantityReceived"
								type="number"
								min="0"
								:max="data.quantity"
								:readonly="!data.receivedEditable"
								:append-icon="!data.receivedEditable ? 'mdi-pencil' : undefined"
								@click:append="data.receivedEditable = true"
								:disabled="!canReceive"
								@input="
									(v) => {
										handleChangeNoSizeReceived(v, data, i);
										data.selected = true;
									}
								"
							></v-text-field>
						</div>
						<div v-else>
							<div
								v-for="(sizeItem, idx) in combinedSizes(data)"
								:key="idx"
								class="size-quantity-row"
							>
								<v-text-field
									class="number-input"
									outlined
									dense
									hide-details="auto"
									v-model.number="sizeItem.quantityReceived"
									type="number"
									min="0"
									:max="sizeItem.quantity"
									:readonly="!sizeItem.receivedEditable"
									:append-icon="!sizeItem.receivedEditable ? 'mdi-pencil' : undefined"
									@click:append="sizeItem.receivedEditable = true"
									:disabled="!canReceive"
									@input="
										(v) => {
											handleChangeReceived(v, sizeItem, i);
											data.selected = true;
										}
									"
								></v-text-field>
							</div>
						</div>
					</div>
				</div>
				<div class="flex-table-cell" role="cell">
					<div v-if="!dropShip" class="size-quantity-wrapper">
						<div v-if="combinedSizes(data).length === 0" class="size-quantity-row">
							<v-text-field
								class="number-input"
								outlined
								dense
								hide-details="auto"
								v-model.number="data.quantityToReceive"
								type="number"
								:min="0"
								:max="data.quantity - data.quantityReceived"
								:disabled="data.quantity === data.quantityReceived || !canReceive"
								@input="
									handleChangeToReceive($event, data);
									data.selected = true;
								"
							></v-text-field>
						</div>
						<div v-else>
							<div
								v-for="(sizeItem, idx) in combinedSizes(data)"
								:key="idx"
								class="size-quantity-row"
							>
								<v-text-field
									class="number-input"
									outlined
									dense
									hide-details="auto"
									v-model.number="sizeItem.quantityToReceive"
									type="number"
									:min="0"
									:max="sizeItem.quantity - sizeItem.quantityReceived"
									:disabled="sizeItem.quantity === sizeItem.quantityReceived || !canReceive"
									@input="
										handleChangeToReceive($event, sizeItem);
										data.selected = true;
									"
								></v-text-field>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import SystemCategoryIcon from "@/components/SystemCategoryIcon.vue";

export default {
	name: "ReceivingEntryTable",
	components: {
		SystemCategoryIcon,
	},
	props: {
		value: {
			type: Array,
			default() {
				return [];
			},
		},
		parent: {
			type: Object,
			default() {
				return null;
			},
		},
		selectAll: {
			type: Boolean,
			default: false,
		},
		canReceive: {
			type: Boolean,
			default: false,
		},
		dropShip: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			snapShot: [],
			tableData: [],
		};
	},
	computed: {
		localTableData: {
			get() {
				return this.tableData;
			},
			set(newValue) {
				this.tableData = newValue;
				this.$emit("updated", newValue);
			},
		},
	},
	methods: {
		// Synchronous — do NOT introduce `await this.$nextTick()` here. The prior async
		// pattern caused an off-by-one: if the user typed a new value and clicked Save
		// quickly, the save built the batch from a still-stale `quantityToReceive`
		// because the handler's update was queued behind the microtask.
		handleChangeReceived(v, item, i) {
			const clamped = Math.min(Math.max(Number(v), 0), item.quantity);
			const row = this.snapShot[i];
			if (!row) return;

			// check for matching size, then special size, in the snapshot row
			const associatedSnapshotItem =
				row.sizes?.find((s) => s.sizeId === item.sizeId && s.lengthId === item.lengthId) ??
				row.specialSizes?.find((s) => s.size === item.size && s.length === item.length);
			if (!associatedSnapshotItem) return;

			item.quantityReceived = clamped;
			item.quantityToReceive =
				Number(associatedSnapshotItem.quantityReceived) === clamped
					? Number(item.quantity) - Number(associatedSnapshotItem.quantityReceived)
					: -Number(associatedSnapshotItem.quantityReceived) + clamped;
		},
		handleChangeNoSizeReceived(v, item, i) {
			const clamped = Math.min(Math.max(Number(v), 0), item.quantity);
			const associatedSnapshotItem = this.snapShot[i];
			if (!associatedSnapshotItem) return;

			item.quantityReceived = clamped;
			item.quantityToReceive =
				Number(associatedSnapshotItem.quantityReceived) === clamped
					? Number(item.quantity) - Number(associatedSnapshotItem.quantityReceived)
					: -Number(associatedSnapshotItem.quantityReceived) + clamped;
		},
		handleChangeToReceive(v, sizeItem) {
			const diff = sizeItem.quantity - sizeItem.quantityReceived - v;
			if (diff < 0) {
				sizeItem.quantityToReceive += diff;
			}
		},
		// Regular and special sizes live in separate arrays but display as one
		// list ordered by the shared displayOrder rank stamped in
		// ReceivingView.refactorResponse (size chart sequence across both kinds).
		combinedSizes(row) {
			return [...row.sizes, ...(row.specialSizes || [])].sort(
				(a, b) =>
					(a.displayOrder ?? Number.MAX_SAFE_INTEGER) -
					(b.displayOrder ?? Number.MAX_SAFE_INTEGER)
			);
		},
		refreshSnapshot() {
			this.snapShot = JSON.parse(JSON.stringify(this.value));
		},
		// Sets quantityToReceive to -originalReceived for every size (and for the
		// no-size case), matching what handleChange* would produce if the user had
		// manually typed 0 in each row. Uses snapShot so edited state is overridden
		// back to the DB baseline. Selects each row so the save picks it up.
		unreceiveAll() {
			if (!Array.isArray(this.snapShot)) return;
			this.localTableData.forEach((data, i) => {
				const snapshotRow = this.snapShot[i];
				if (!snapshotRow) return;

				const sizes = [...(data.sizes || []), ...(data.specialSizes || [])];
				if (sizes.length === 0) {
					const originalReceived = Number(snapshotRow.quantityReceived || 0);
					if (originalReceived > 0) {
						data.quantityReceived = 0;
						data.quantityToReceive = -originalReceived;
					}
				} else {
					sizes.forEach((sizeItem) => {
						const snapSize =
							snapshotRow.sizes?.find(
								(s) => s.sizeId === sizeItem.sizeId && s.lengthId === sizeItem.lengthId
							) ??
							snapshotRow.specialSizes?.find(
								(s) => s.size === sizeItem.size && s.length === sizeItem.length
							);
						if (!snapSize) return;
						const originalReceived = Number(snapSize.quantityReceived || 0);
						if (originalReceived > 0) {
							sizeItem.quantityReceived = 0;
							sizeItem.quantityToReceive = -originalReceived;
						}
					});
				}

				this.$set(data, "selected", true);
			});
		},
	},
	watch: {
		selectAll: {
			handler: function () {
				if (Array.isArray(this.value)) {
					this.value.forEach((item) => {
						if (!this.dropShip && this.canReceive) {
							this.$set(item, "selected", this.selectAll);
						}
					});
				}
			},
			deep: true,
		},
		// Keep the snapshot in sync with the underlying data after parent re-fetches
		// (e.g., after a batch save completes). Prior behavior captured snapshot only
		// at `mounted()`, which left it stale and caused re-receive bugs after the
		// user unreceived and then saved again without navigating away.
		value: {
			handler: function () {
				this.refreshSnapshot();
			},
			immediate: true,
		},
	},
	mounted() {
		// snapShot is initialized via the `value` watcher (immediate: true).
		this.tableData = this.value;
	},
};
</script>

<style lang="scss" scoped>
.entry {
	&-table {
		position: relative;
		overflow-y: auto;
		background-color: $color-white;
		border: 1px solid #bbbbbb;
		border-radius: 3px;

		.form-input {
			margin: 0;
		}

		.flex-table {
			border: none;

			&[role="table-head"] {
				position: sticky;
				top: 0;
				z-index: 1;

				.flex-table-row {
					background-color: rgba(224, 224, 224, 0.6);
				}
			}

			&[role="table-body"] {
				.flex-table-row {
					border-top: none;
					border-bottom: solid 1px $color-gray;
				}
			}
		}

		.flex-table-cell[role="column-header"] {
			font-size: 1.4rem;
		}

		.flex-table-cell {
			font-size: 1.6rem;
			display: block;

			&:nth-child(1) {
				width: 5%;
			}

			&:nth-child(2) {
				width: 20%;
			}

			&:nth-child(3) {
				width: 15%;
			}

			&:nth-child(4) {
				width: 15%;
			}

			&:nth-child(5) {
				width: 15%;
			}

			&:nth-child(6) {
				width: 15%;
			}

			&:nth-child(7) {
				width: 15%;
			}
		}
	}
}

.size-quantity-wrapper {
	padding: 8px 0;
}

.size-quantity-row {
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;

	.v-text-field {
		margin-top: 0;
		margin-bottom: 0;
	}
}

.number-input {
	width: 100%;
}
</style>

<style lang="scss">
.parent-item {
	padding-left: 25px;
}
</style>
